import React, { useState, useEffect, useCallback } from 'react';
import { DollarSign, TrendingUp, TrendingDown, Clock, Eye, EyeOff, Settings } from 'lucide-react';
import { tradingApi } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import ErrorDisplay from './ErrorDisplay';
import GatewayConfigModal from './GatewayConfigModal';
import GatewayControlButton from './GatewayControlButton';
import { formatDateTimeET } from '../utils/datetime';
import { formatCurrency } from '../utils/currency';
import { useAuth } from '../context/AuthContext';
import { usePortfolioQuery } from '../store/tradingApiSlice';

interface InvestorAccountSummaryProps {
    refreshInterval?: number;
    isConnected?: boolean;
}

const InvestorAccountSummary: React.FC<InvestorAccountSummaryProps> = ({ refreshInterval = 30000, isConnected = true }) => {
    const { user } = useAuth();
    const isInvestor = user?.role === 'investor';
    const {
        data: portfolio = null,
        isLoading: loading,
        isFetching: refreshing,
        error: portfolioError,
        refetch: refetchPortfolio,
    } = usePortfolioQuery(undefined, {
        pollingInterval: isConnected ? refreshInterval : 0,
    });
    const blockingError = portfolioError && !portfolio ? 'Failed to fetch account data' : null;
    const refreshError = portfolioError && portfolio ? 'Live refresh failed; keeping the last account values.' : null;
    const [hideValues, setHideValues] = useState<boolean>(() => {
        if (typeof window === 'undefined') return false;
        const saved = window.localStorage.getItem('accountSummaryHideValues');
        return saved === 'true';
    });

    const [showConfigModal, setShowConfigModal] = useState(false);
    const [controlState, setControlState] = useState<any>(null);
    const [configLoading, setConfigLoading] = useState(false);
    const [gatewayError, setGatewayError] = useState<string | null>(null);

    // Auto-clear gateway error after 8 seconds
    useEffect(() => {
        if (gatewayError) {
            const timer = setTimeout(() => setGatewayError(null), 8000);
            return () => clearTimeout(timer);
        }
    }, [gatewayError]);

    const fetchControlState = useCallback(async (silent = false) => {
        if (!silent) setConfigLoading(true);
        try {
            const response = await tradingApi.getSystemControlState();
            if (response.success && response.data) {
                setControlState(response.data);
                if (!silent) setShowConfigModal(true);
            }
        } catch (err) {
            console.warn("Failed to fetch control state:", err);
            if (!silent) setShowConfigModal(true);
        } finally {
            if (!silent) setConfigLoading(false);
        }
    }, []);

    const hasAccountData = Boolean(
        portfolio?.data?.account_type &&
        portfolio.data.account_type !== 'N/A' &&
        portfolio.data.net_liquidation !== undefined &&
        portfolio.data.net_liquidation !== null
    );
    const isEffectivelyConnected = portfolio?.data?.connection_status === 'connected' ||
        (isConnected && hasAccountData);
    const isInitializing = portfolio?.data?.connection_status === 'initializing';
    const isUnavailable = portfolio?.data?.connection_status === 'unavailable';
    const hasSnapshot = portfolio?.data?.snapshot_available !== false;

    useEffect(() => {
        if (!isInitializing) return;
        const retryId = window.setTimeout(() => refetchPortfolio(), 2000);
        return () => window.clearTimeout(retryId);
    }, [isInitializing, portfolio?.timestamp, refetchPortfolio]);

    useEffect(() => {
        // Always fetch control state silently to see if we have gateway controls available
        fetchControlState(true);
    }, [fetchControlState]);

    // Polling for control state if modal is open OR if we are showing the start button (waiting for connect)
    useEffect(() => {
        let intervalId: ReturnType<typeof setTimeout>;
        if (showConfigModal || (!controlState?.ib_connected && controlState?.gateway_configured)) {
            intervalId = setInterval(() => fetchControlState(true), 5000);
        }
        return () => { if (intervalId) clearInterval(intervalId); };
    }, [showConfigModal, controlState?.ib_connected, controlState?.gateway_configured, fetchControlState]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.localStorage.setItem('accountSummaryHideValues', hideValues ? 'true' : 'false');
        }
    }, [hideValues]);

    const isConfigured = controlState?.gateway_configured;
    const currency = portfolio?.data?.currency || 'USD';

    const isStale = !!portfolio?.data?.stale;
    // A persisted snapshot remains useful when IB is disconnected. Only hide
    // values when the backend confirms that no account snapshot exists.
    const canDisplayValues = hasSnapshot && hasAccountData;

    return (
        <div className={`panel full-bleed-panel w-full lg:h-full relative transition-all duration-300 ${isStale ? 'border-warning-500/20 bg-warning-500/5' : ''}`}>
            <div className="px-6 py-5 flex flex-col sm:flex-row sm:items-center justify-between border-b border-white/10 relative z-10 gap-4">
                <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-xl font-bold text-white">
                            Portfolio Overview
                        </h2>
                        <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded-full border ${isInitializing
                                ? 'bg-warning-500/10 text-warning-400 border-warning-500/20'
                                : isEffectivelyConnected
                                ? (isStale 
                                    ? 'bg-warning-500/10 text-warning-400 border-warning-500/20' 
                                    : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20')
                                : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>
                                {isInitializing ? 'Connecting' : isUnavailable ? 'Account Unavailable' : isStale ? 'Stale Data' : (isEffectivelyConnected ? 'Connected' : (isConnected ? 'Not Configured' : 'Offline'))}
                            </span>
                             {isStale && (
                                <div className="relative group cursor-help z-50">
                                    <span className="text-[10px] uppercase font-black tracking-wider px-2 py-0.5 rounded-sm bg-warning-500/20 text-warning-500 border border-warning-500/30 animate-pulse">
                                        Stale
                                    </span>
                                    <div className="absolute right-0 top-full mt-2 hidden group-hover:block w-64 p-3 bg-surface border border-warning-500/30 rounded-xl shadow-xl text-xs font-normal text-warning-500 leading-normal normal-case pointer-events-none">
                                        <div className="absolute bottom-full right-4 border-4 border-transparent border-b-surface" />
                                        <p className="font-bold mb-1">{isInitializing ? 'Synchronizing Account' : isUnavailable ? 'Account Data Unavailable' : 'Portfolio Data Stale'}</p>
                                        <p className="opacity-80">
                                            {isInitializing
                                                ? 'IB is connected and account data is still synchronizing. This panel will refresh automatically.'
                                                : isUnavailable
                                                ? 'The IB socket is online, but account values did not arrive. The backend will keep retrying automatically.'
                                                : 'Live streaming is currently offline. Displaying last cached values recorded when connection was active.'}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <p className="text-sm text-muted">
                        {isInitializing ? 'Connecting to IB and synchronizing account data' : isUnavailable ? 'IB connected — account values currently unavailable' : isStale ? 'Showing offline cached snapshot' : 'Real-time account performance'}
                    </p>
                </div>
                <div className="flex items-center justify-between sm:justify-end gap-3 w-full sm:w-auto">
                    {/* Actions: Show if NOT connected OR if connected and we have control capability */}
                    {!isInvestor && !isInitializing && (!isEffectivelyConnected || (controlState && isConfigured)) && (
                        <>
                            {controlState && isConfigured ? (
                                <div className="flex items-center gap-2">
                                    <GatewayControlButton
                                        controlState={controlState}
                                        onRefresh={async () => { await fetchControlState(true); }}
                                        onConfigureNeeded={() => setShowConfigModal(true)}
                                        onError={(msg) => setGatewayError(msg)}
                                        variant="slim"
                                        className="h-10 text-sm px-4 min-w-[120px]"
                                    />
                                    <button
                                        onClick={() => setShowConfigModal(true)}
                                        className="h-10 w-10 flex items-center justify-center rounded-xl bg-white/5 border border-white/10 text-muted hover:text-white hover:bg-white/10 transition"
                                        title="Configure Connection"
                                    >
                                        <Settings size={18} />
                                    </button>
                                </div>
                            ) : (
                                !isEffectivelyConnected && (
                                    <button
                                        onClick={() => fetchControlState(false)}
                                        disabled={configLoading}
                                        className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium shadow-lg shadow-emerald-500/20 transition-all disabled:opacity-50 disabled:shadow-none"
                                    >
                                        {configLoading ? (
                                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        ) : (
                                            <Settings size={16} />
                                        )}
                                        Configure Connection
                                    </button>
                                )
                            )}
                            <div className="h-8 w-px bg-white/10 mx-1" />
                        </>
                    )}

                    <button
                        onClick={() => setHideValues((prev) => !prev)}
                        className="p-2.5 rounded-xl border border-white/10 bg-white/5 text-muted hover:text-white hover:bg-white/10 transition flex items-center justify-center"
                        aria-label={hideValues ? 'Show account values' : 'Hide account values'}
                        title={hideValues ? 'Show values' : 'Hide values'}
                    >
                        {hideValues ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                </div>
            </div>

            {/* Gateway Error Notification - Animates in below header */}
            {gatewayError && (
                <div className="bg-red-500/10 border-b border-red-500/20 px-6 py-2 flex items-center justify-between animate-in slide-in-from-top-2 duration-200">
                    <div className="flex items-center gap-2 text-sm text-red-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                        {gatewayError}
                    </div>
                    <button
                        onClick={() => setGatewayError(null)}
                        className="text-xs text-red-400/70 hover:text-red-300"
                    >
                        Dismiss
                    </button>
                </div>
            )}

            <div className="p-6">
                {loading && !portfolio && !blockingError && (
                    <div className="flex justify-center py-8">
                        <LoadingSpinner size="lg" />
                    </div>
                )}

                {blockingError && (
                    <ErrorDisplay
                        error={blockingError}
                        onRetry={() => refetchPortfolio()}
                        message="Failed to load account data"
                    />
                )}

                {refreshError && (
                    <div className="mb-4 rounded-xl border border-warning-500/20 bg-warning-500/5 px-4 py-3 text-xs text-warning-300">
                        {refreshError}
                    </div>
                )}

                {portfolio?.success && !blockingError && isStale && !hasSnapshot && (
                    <div className="rounded-xl border border-warning-500/20 bg-warning-500/5 px-4 py-8 text-center text-sm text-warning-300/80">
                        {isInitializing
                            ? 'Connecting to IB and synchronizing this account…'
                            : isUnavailable
                            ? 'IB is connected, but account values are unavailable. Synchronization will continue in the background.'
                            : 'Connect this account once to create its first persisted account snapshot.'}
                    </div>
                )}

                {portfolio?.success && !blockingError && (!isStale || hasSnapshot) && (
                    <div className="space-y-6">


                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Net Liquidation - Prominent */}
                            <div className={`rounded-xl transition duration-300 p-5 relative overflow-hidden group ${
                                isStale
                                    ? 'bg-warning-500/5 border border-warning-500/20 hover:border-warning-500/35'
                                    : 'bg-white/10 border border-white/10 hover:border-neon/30'
                            }`}>
                                <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition">
                                    <DollarSign size={48} />
                                </div>
                                <p className="text-sm font-medium text-gray-400 mb-1">
                                    Net Liquidation Value
                                </p>
                                <p className={`text-3xl font-bold text-white tracking-tight ${refreshing ? 'animate-pulse' : ''}`}>
                                    {!canDisplayValues ? '---' : formatCurrency(portfolio.data?.net_liquidation, currency, hideValues)}
                                </p>
                                <div className="mt-4 flex items-center gap-2 text-xs text-muted">
                                    <Clock size={12} />
                                    <span>Updated: {formatDateTimeET(portfolio.data?.timestamp)}</span>
                                </div>
                            </div>

                            {/* Realized P&L */}
                            <div className={`rounded-xl transition duration-300 p-5 ${
                                isStale
                                    ? 'bg-warning-500/5 border border-warning-500/20 hover:border-warning-500/35'
                                    : 'bg-white/5 border border-white/10 hover:bg-white/10'
                            }`}>
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm font-medium text-gray-400">
                                        Realized P&L (Today)
                                    </p>
                                    {portfolio.data?.realized_pnl_today !== undefined && portfolio.data?.realized_pnl_today < 0 ?
                                        <TrendingDown size={16} className="text-red-400" /> :
                                        <TrendingUp size={16} className="text-emerald-400" />
                                    }
                                </div>
                                <p className={`text-2xl font-bold tracking-tight ${(portfolio.data?.realized_pnl_today || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
                                    } ${refreshing ? 'animate-pulse' : ''}`}>
                                    {!canDisplayValues ? '---' : formatCurrency(portfolio.data?.realized_pnl_today, currency, hideValues)}
                                </p>
                            </div>

                            {/* Unrealized P&L */}
                            <div className={`rounded-xl transition duration-300 p-5 ${
                                isStale
                                    ? 'bg-warning-500/5 border border-warning-500/20 hover:border-warning-500/35'
                                    : 'bg-white/5 border border-white/10 hover:bg-white/10'
                            }`}>
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-sm font-medium text-gray-400">
                                        Unrealized P&L (Today)
                                    </p>
                                    {portfolio.data?.unrealized_pnl_today !== undefined && portfolio.data?.unrealized_pnl_today < 0 ?
                                        <TrendingDown size={16} className="text-red-400" /> :
                                        <TrendingUp size={16} className="text-emerald-400" />
                                    }
                                </div>
                                <p className={`text-2xl font-bold tracking-tight ${(portfolio.data?.unrealized_pnl_today || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'
                                    } ${refreshing ? 'animate-pulse' : ''}`}>
                                    {!canDisplayValues ? '---' : formatCurrency(portfolio.data?.unrealized_pnl_today, currency, hideValues)}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <GatewayConfigModal
                isOpen={showConfigModal}
                onClose={() => setShowConfigModal(false)}
                onSuccess={() => {
                    setShowConfigModal(false);
                    refetchPortfolio(); // Refresh portfolio data after config
                }}
                currentUsername={controlState?.ib_username}
                currentMode={controlState?.ib_mode}
                savedGateways={controlState?.saved_gateways}
            />
        </div>
    );
};

export default InvestorAccountSummary;
